import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, UPLOADS } from "../../config/api";
export default function Designer() {
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [user, setUser] = useState(null);
  const [skills, setSkills] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [experience, setExperience] = useState("");
  const [salary, setSalary] = useState("");

  /* =========================================================
      1. PAGINATION STATE
     ========================================================= */
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(40); // Number of designers per page

  /* =========================================================
      2. FETCH DESIGNERS (Backend Sync)
     ========================================================= */
  const fetchDesigners = useCallback(async () => {
    try {
      const res = await fetch(
        `${API.LIST_ACTIVE_DESIGNERS}&skills=${skills.join(",")}&t=${new Date().getTime()}`,
      );
      const data = await res.json();
      setDesigners(data);
    } catch (error) {
      console.log("Fetch Error:", error);
    }
  }, [skills]);

  useEffect(() => {
    fetchDesigners();
    const interval = setInterval(fetchDesigners, 5000);
    window.addEventListener("focus", fetchDesigners);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", fetchDesigners);
    };
  }, [fetchDesigners]);

  /* =========================================================
      3. USER & WISHLIST SYNC
     ========================================================= */
  const syncData = useCallback(() => {
    const loginUser = JSON.parse(sessionStorage.getItem("user"));
    setUser(loginUser);

    if (loginUser) {
      const saved =
        JSON.parse(localStorage.getItem(`wishlist_${loginUser.id}`)) || [];
      setWishlist(saved);
    } else {
      setWishlist([]);
    }
  }, []);

  useEffect(() => {
    syncData();
    window.addEventListener("cartUpdated", syncData);
    window.addEventListener("storage", syncData);
    window.addEventListener("userLogin", syncData);
    window.addEventListener("userLogout", syncData);

    return () => {
      window.removeEventListener("cartUpdated", syncData);
      window.removeEventListener("storage", syncData);
      window.removeEventListener("userLogin", syncData);
      window.removeEventListener("userLogout", syncData);
    };
  }, [syncData]);

  /* =========================
      4. HANDLERS & FILTER LOGIC
     ========================= */
  const handleSkill = (value) => {
    if (skills.includes(value)) {
      setSkills(skills.filter((item) => item !== value));
    } else {
      setSkills([...skills, value]);
    }
  };

  const toggleWishlist = (item) => {
    if (!user) return alert("Please login first");
    const storageKey = `wishlist_${user.id}`;
    let updated = JSON.parse(localStorage.getItem(storageKey)) || [];
    const index = updated.findIndex(
      (w) => w.id.toString() === item.id.toString() && w.type === "designer",
    );

    if (index !== -1) {
      updated.splice(index, 1);
    } else {
      updated.push({ ...item, type: "designer" });
    }

    localStorage.setItem(storageKey, JSON.stringify(updated));
    setWishlist(updated);
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const filteredDesigners = designers.filter((item) => {
    const matchSkill =
      skills.length === 0 ||
      skills.some((skill) =>
        item.skills?.toLowerCase().includes(skill.toLowerCase()),
      );

    const matchCity =
      selectedCity === "" ||
      item.location?.toLowerCase().includes(selectedCity.toLowerCase());
    const matchExperience =
      experience === "" ||
      (() => {
        const expValue = (item.experience || "").toLowerCase().trim();

        let years = 0;
        let months = 0;

        // Handle format: "8 Mon" or "2 Yrs" or "2 Yrs/8 Mon"
        if (expValue.includes("/")) {
          // Split "2 Yrs/8 Mon"
          const parts = expValue.split("/").map((p) => p.trim());

          // First part: Years (2 Yrs)
          const yearPart = parts[0];
          const yearMatch = yearPart.match(/(\d+)\s*yrs?/i);
          if (yearMatch) years = parseInt(yearMatch[1]);

          // Second part: Months (8 Mon)
          const monthPart = parts[1];
          const monthMatch = monthPart.match(/(\d+)\s*mon/i);
          if (monthMatch) months = parseInt(monthMatch[1]);
        } else {
          // Single value: "8 Mon" or "2 Yrs"
          const yearMatch = expValue.match(/(\d+)\s*yrs?/i);
          if (yearMatch) years = parseInt(yearMatch[1]);

          const monthMatch = expValue.match(/(\d+)\s*mon/i);
          if (monthMatch) months = parseInt(monthMatch[1]);
        }

        const totalMonths = years * 12 + months;

        console.log(
          `"${item.experience}" -> ${years}Yrs ${months}Mon = ${totalMonths} months`,
        );

        if (experience === "0-2") {
          return totalMonths >= 0 && totalMonths <= 24;
        }
        if (experience === "3-5") {
          return totalMonths > 24 && totalMonths <= 60;
        }
        if (experience === "5") {
          return totalMonths > 60;
        }
        return true;
      })();
    return matchSkill && matchCity && matchExperience;
  });
  // Reset to page 1 whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [skills, selectedCity, experience, itemsPerPage]);
  /* =========================
      5. PAGINATION CALCULATIONS
     ========================= */
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDesigners = filteredDesigners.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredDesigners.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="shop-page container-fluid py-4">
      <div className="row">
        {/* LEFT FILTER */}
        <div className="col-lg-2 d-none d-lg-block">
          <div className="filter-sidebar shadow-sm p-3 bg-white rounded sticky-top">
            <h5 className="fw-bold mb-3">Filters</h5>
            <div className="filter-box mb-4">
              <h6>Skills</h6>
              {["Multi", "cording", "Sequin", "chain", "beads"].map((s) => (
                <label key={s} className="d-block mb-1">
                  <input
                    type="checkbox"
                    className="me-2"
                    checked={skills.includes(s)}
                    onChange={() => handleSkill(s)}
                  />{" "}
                  {s} Head
                </label>
              ))}
            </div>
            <div className="filter-box mb-4">
              <h6>Experience</h6>
              {["0-2", "3-5", "5"].map((exp) => (
                <label key={exp} className="d-block mb-1">
                  <input
                    type="radio"
                    name="exp"
                    className="me-2"
                    checked={experience === exp}
                    onChange={() => setExperience(exp)}
                  />
                  {exp === "5" ? "5+ Years" : `${exp} Years`}
                </label>
              ))}
            </div>
            {/* <div className="mb-3 p-3 bg-light rounded">
  <button 
    className="btn btn-warning btn-sm me-2"
    onClick={() => {
      const experiences = [...new Set(designers.map(d => d.experience))];
      console.table({
        totalDesigners: designers.length,
        uniqueExperiences: experiences,
        countByExp: experiences.reduce((acc, exp) => {
          acc[exp] = designers.filter(d => d.experience === exp).length;
          return acc;
        }, {})
      });
    }}
  >
    🔍 Show All Experience Formats
  </button>
  
  <button 
    className="btn btn-info btn-sm"
    onClick={() => {
      const monDesigners = designers.filter(d => 
        d.experience && d.experience.toLowerCase().includes('mon')
      );
      console.log('🎨 "Mon" Designers:', monDesigners.map(d => ({name: d.name, exp: d.experience})));
    }}
  >
    🔍 Find "Mon" Designers
  </button>
</div> */}
            <div className="filter-box mt-4">
              <h6>City</h6>
              <input
                type="text"
                placeholder="Search City"
                className="form-control"
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
              />
            </div>

            <button
              className="btn btn-sm btn-outline-secondary w-100"
              onClick={() => {
                setSkills([]);
                setExperience("");
                setSalary("");
                setSelectedCity("");
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
        {/* MOBILE HORIZONTAL FILTER */}
        <div className="d-lg-none mb-3">
          <div className="mobile-filter-bar">
            {/* Skills */}
            {["Multi", "cording", "Sequin", "chain", "beads"].map((skill) => (
              <button
                key={skill}
                className={`filter-chip ${skills.includes(skill) ? "active" : ""}`}
                onClick={() => handleSkill(skill)}
              >
                {skill}
              </button>
            ))}

            {/* Experience */}
            <button
              className={`filter-chip ${experience === "0-2" ? "active" : ""}`}
              onClick={() => setExperience("0-2")}
            >
              0-2 Yr
            </button>

            <button
              className={`filter-chip ${experience === "3-5" ? "active" : ""}`}
              onClick={() => setExperience("3-5")}
            >
              3-5 Yr
            </button>

            <button
              className={`filter-chip ${experience === "5" ? "active" : ""}`}
              onClick={() => setExperience("5")}
            >
              5+ Yr
            </button>

            {/* City Dropdown */}
            <select
              className="mobile-select"
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
            >
              <option value="">All Cities</option>
              {[...new Set(designers.map((d) => d.location))].map((city, i) => (
                <option key={i} value={city}>
                  {city}
                </option>
              ))}
            </select>

            {/* Clear */}
            <button
              className="filter-chip clear"
              onClick={() => {
                setSkills([]);
                setExperience("");
                setSelectedCity("");
              }}
            >
              Clear
            </button>
          </div>
        </div>
        {/* CENTER CONTENT */}
        <div className="col-lg-8">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <h2
              className="section-title mb-0"
              style={{ color: "#00334e", fontWeight: "700" }}
            >
              Embroidery Designers
            </h2>

            <div className="d-flex align-items-center gap-3">
              {/* City Filter */}
              {/* <div style={{ width: "180px" }}>
      <select 
        className="form-select form-select-sm"
        value={selectedCity} 
        onChange={(e) => setSelectedCity(e.target.value)}
      >
        <option value="">All Cities</option>
        {[...new Set(designers.map(d => d.location))].map((city, i) => (
          <option key={i} value={city}>{city}</option>
        ))}
      </select>
    </div> */}

              {/* Limit Select */}
              <div className="d-flex align-items-center gap-2">
                {/* <span className="small text-muted">Show</span> */}

                <select
                  className="form-select form-select-sm"
                  style={{ width: "80px" }}
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
                >
                  <option value={20}>20</option>
                  <option value={40}>40</option>
                  <option value={60}>60</option>
                  <option value={100}>100</option>
                </select>

                {/* <span className="small text-muted">per page</span> */}
              </div>
            </div>
          </div>
          <div className="designs-grid">
            {currentDesigners.length > 0 ? (
              currentDesigners.map((item) => {
                const isLiked = wishlist.some(
                  (w) =>
                    w.id.toString() === item.id.toString() &&
                    w.type === "designer",
                );
                return (
                  <div className="design-card" key={item.id}>
                    <div className="machine-img-container">
                      <img
                        src={`${UPLOADS.DESIGNER_IMAGE}${item.profile}`}
                        className="designer-img"
                        alt={item.name}
                        style={{
                          height: "100%",
                          objectFit: "cover",
                          width: "100%",
                        }}
                      />
                    </div>
                    <div className="machine-body">
                      <h4 className="machine-name">{item.name}</h4>
                      <div className="d-flex align-items-center justify-content-between mt-2">
                        <div className="price-block">
                          <span className="currency-symbol">₹</span>
                          <span className="amount">{item.expected_salary}</span>
                          <span className="text-muted small"> / hour</span>
                        </div>
                        <button
                          className="contact-btn"
                          onClick={() =>
                            navigate(`/designerdetails/${item.id}`)
                          }
                        >
                          <i className="bi bi-telephone-fill me-1"></i> Contact
                        </button>
                      </div>
                      <div className="spec-row mt-3">
                        <div className="spec-item">
                          <span className="spec-label">Skills:</span>
                          <span className="spec-value">{item.skills}</span>
                        </div>

                        <div className="spec-item">
                          <span className="spec-label">Exp:</span>
                          <span className="spec-value">{item.experience}</span>
                        </div>
                      </div>
                      <div className="location-info mt-1">
                        <i className="bi bi-geo-alt-fill text-secondary me-1"></i>{" "}
                        {item.location}
                      </div>
                      <div className="machine-footer mt-1">
                        <button
                          className="more-details-btn"
                          onClick={() =>
                            navigate(`/designerdetails/${item.id}`)
                          }
                        >
                          <i className="bi bi-person-badge-fill me-2"></i> Hire
                          Now
                        </button>
                        <button
                          className="footer-like-btn"
                          onClick={() => toggleWishlist(item)}
                        >
                          <i
                            className={`bi ${isLiked ? "bi-heart-fill text-danger" : "bi-heart"}`}
                          ></i>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center w-100 py-5 bg-white rounded shadow-sm">
                <p className="text-muted m-0">
                  No designers found matching your criteria.
                </p>
              </div>
            )}
          </div>
          <div className="d-lg-none mt-4">
            <div className="mobile-ads p-3 bg-white shadow-sm rounded text-center">
              <h6 className="fw-bold">Sponsored</h6>

              <div className="ad-card mt-2">
                <div className="ad-placeholder">
                  <p className="small text-muted">Your Ad Here</p>
                </div>
                <p className="small mt-2">Premium Threads Sale!</p>
              </div>
            </div>
          </div>
          {/* ================= PAGINATION UI ================= */}
          {totalPages > 1 && (
            <nav className="d-flex justify-content-center mt-5">
              <ul className="pagination custom-pagination">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>

                {[...Array(totalPages)].map((_, i) => (
                  <li
                    key={i + 1}
                    className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => paginate(i + 1)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}

                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>

        {/* RIGHT ADS */}
        <div className="col-lg-2">
          <div className="ads-sidebar p-3 bg-white shadow-sm rounded text-center sticky-top">
            <h5 className="ads-title">Sponsored</h5>
            <div className="ad-card mt-3">
              <div className="ad-placeholder">
                <p className="small text-muted">Your Ad Here</p>
              </div>
              <p className="small mt-2">Premium Threads Sale!</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .custom-pagination .page-link {
          color: #00a699;
          border: 1px solid #dee2e6;
          margin: 0 3px;
          border-radius: 4px;
          font-weight: 500;
        }
        .custom-pagination .page-item.active .page-link {
          background-color: #00a699;
          border-color: #00a699;
          color: white;
        }
        .custom-pagination .page-link:hover {
          background-color: #f1f1f1;
          color: #00847a;
        }
      `}</style>
    </div>
  );
}
